/**
 * Town-boss encounter substrate (HEX-122 / HEX-125 / ADR-0018 §4).
 *
 * Every frontier town is boss-held until liberated. Entering an `evil` town
 * dispatches a card-combat fight against a named boss from the bespoke roster
 * (HEX-125), scaled by
 *
 *     tier index = tierBand(originDistance) + biomeEvilWeight(biome) + accumulatedEvilOffset
 *
 * — distance is the primary danger axis (ADR-0018 §1); the biome adds a small
 * evil bump so the same ring of towns isn't uniformly tuned, and accumulatedEvil
 * (HEX-123) makes re-challenge costlier the longer a town festers evil. Boss
 * identity (name, glyph, deck) is fixed per town via `selectBoss`; HP is always
 * driven by the distance tier × BOSS_HP_MULTIPLIER so danger grows outward.
 *
 * Victory marks the town `liberated` (see `liberateTown` in `townState.ts`) and
 * materialises its reserved pillar slot. Defeat leaves the town untouched and
 * archives the character through the shared `resolveCombatOutcome` defeat path.
 *
 * Evil-weight reconciliation (HEX-123):
 *   BIOME_EVIL_WEIGHT (integer 0–2) adds to the discrete tier-band index — a
 *   tier step, not a continuous scalar. The fractional EVIL_WEIGHT (0.1–0.5)
 *   from src/worldgen/biome-transition.ts feeds evilPressure.ts's corruption()
 *   term instead. Both exist intentionally; merging them would misscale one.
 */

import type { CardCombatState } from '../combat/cardEngine';
import {
  type CombatOutcome,
  POST_VICTORY_REGEN_DIVISOR,
  combatSeedFor,
  resolveCombatOutcome,
} from '../combat/encounter';
import { buildEnemy } from '../combat/enemies';
import type { EnemyTemplate } from '../combat/enemies';
import { makePlayerCombatant } from '../combat/player';
import type { CombatDrops, EnemyTier } from '../combat/types';
import type { Encounter } from '../combat/ui/CardCombatOverlay';
import type { Character } from '../core/character';
import type { Biome } from '../core/tile';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';
import { originDistance } from '../worldgen/distance';
import { selectBoss } from './bossRoster';
import { TIER_ORDER, tierBand } from './tierBands';
import { type LiberateTownDeps, liberateTown } from './townState';

/**
 * Per-biome evil weight (HEX-122). Adds to the distance band to bump the boss
 * tier — open plains host the gentlest bosses, the hostile biomes the meanest.
 *
 * These are INTEGER tier-band offsets (0–2), distinct from EVIL_WEIGHT in
 * biome-transition.ts which is a fractional corruption scalar (0.1–0.5). See
 * module header for the reconciliation rationale.
 */
export const BIOME_EVIL_WEIGHT: Readonly<Record<Biome, number>> = {
  plain: 0,
  forest: 1,
  snow: 1,
  lake: 2,
  mountain: 2,
};

export function biomeEvilWeight(biome: Biome): number {
  return BIOME_EVIL_WEIGHT[biome];
}

/** Boss HP multiplier over the tier baseline — bosses out-stat a stray wild
 * encounter so a town is a real wall, not a speed bump. */
export const BOSS_HP_MULTIPLIER = 1.5;

/**
 * Canonical HP baseline per tier for boss HP scaling (HEX-125). Drives the
 * distance-scaled HP budget independently of which named boss is selected.
 * Values align with the first-registered enemy of each tier (the stable anchor)
 * so boss HP is backwards-compatible with the HEX-122 placeholder.
 */
const BOSS_TIER_BASE_HP: Record<EnemyTier, number> = {
  common: 12,
  rare: 26,
  epic: 36,
  legendary: 53,
};

/**
 * Boss tier for a town tile: the distance band plus the biome evil bump plus
 * any accumulated-evil offset from festering (HEX-123 / ADR-0018 §5), clamped
 * to the strongest tier. `extraTierOffset` comes from `accumulatedEvil()` in
 * `evilPressure.ts` — pass 0 for a first liberation or when no context exists.
 * Towns spawn only beyond `SETTLEMENT_MIN_ORIGIN_DISTANCE`, so the weakest real
 * boss is at least the band of that ring.
 */
export function bossTierFor(tile: Tile, extraTierOffset = 0): EnemyTier {
  const d = originDistance(tile.q, tile.r);
  const idx = Math.min(
    tierBand(d) + biomeEvilWeight(tile.biome) + extraTierOffset,
    TIER_ORDER.length - 1,
  );
  return TIER_ORDER[idx] ?? TIER_ORDER[TIER_ORDER.length - 1] ?? 'legendary';
}

/**
 * Build the boss template for a town. Boss identity (name, glyph, deck) is
 * selected deterministically from the named roster via `selectBoss`; HP is
 * always driven by `bossTierFor` × `BOSS_HP_MULTIPLIER` so danger scales with
 * distance regardless of which boss archetype appears.
 *
 * `extraTierOffset` threads through from `accumulatedEvil` (HEX-123 §5): a town
 * left festering evil scales up to a harder tier (and thus HP).
 */
export function buildBossTemplate(tile: Tile, extraTierOffset = 0): EnemyTemplate {
  const tier = bossTierFor(tile, extraTierOffset);
  const seed = Math.abs((tile.q * 73856093) ^ (tile.r * 19349663));
  const boss = selectBoss(seed, tile.biome);
  return {
    ...boss,
    hp: Math.ceil(BOSS_TIER_BASE_HP[tier] * BOSS_HP_MULTIPLIER),
  };
}

/**
 * Build the `CardCombatOverlay` `Encounter` payload for a town boss fight.
 * Player HP carries (ADR-0015 §5); the enemy is the buffed boss template; the
 * seed reuses `combatSeedFor` so a retreat-and-re-enter draws a fresh stream
 * (HEX-87 crossings nonce) and two characters fight different dice.
 *
 * `extraTierOffset` is floored accumulated evil from `evilPressure.ts` — pass
 * 0 (default) when no context is available.
 */
export function buildBossEncounter(
  character: Character,
  tile: Tile,
  worldSeed: number,
  extraTierOffset = 0,
): Encounter {
  const template = buildBossTemplate(tile, extraTierOffset);
  return {
    player: makePlayerCombatant(character),
    enemy: buildEnemy(template),
    seed: combatSeedFor(character, tile, worldSeed),
    enemyTier: template.tier,
    enemyGlyph: template.glyph,
  };
}

export interface ResolveBossDeps extends LiberateTownDeps {
  readonly persistence: Pick<Persistence, 'saveTileMutations' | 'saveCharacter'>;
  /** Death-stamp clock; defaults to `Date.now`. Tests inject a fixed clock. */
  readonly now?: () => number;
}

/**
 * Close the loop after a town-boss combat ends.
 *
 * Victory: carry the survivor HP back (+ post-victory regen), award the boss
 * gold drop, then `liberateTown` — marks the town `liberated` and materialises
 * its reserved pillar slot. Both tiles persist.
 *
 * Defeat: delegate to the shared `resolveCombatOutcome` defeat path — archives
 * the character (death stamp + deck kept) and bumps the town tile's
 * `fallenCount`. The town state is UNCHANGED (still `evil`); a future character
 * can retry the boss.
 *
 * `pillarSlotTile` MUST be the reserved `ground` tile at
 * `townTile.lifecycleState.anchorTiles.pillar` so liberation can upgrade it.
 */
export async function resolveBossOutcome(
  state: CardCombatState,
  character: Character,
  townTile: Tile,
  pillarSlotTile: Tile,
  deps: ResolveBossDeps,
  drops?: CombatDrops,
): Promise<CombatOutcome> {
  if (state.victor === null) {
    throw new Error('resolveBossOutcome called before combat ended');
  }
  if (state.victor === 'player') {
    const regen = Math.ceil(state.player.maxHp / POST_VICTORY_REGEN_DIVISOR);
    character.hp = Math.min(state.player.maxHp, state.player.hp + regen);
    if (drops !== undefined && drops.gold > 0) character.gold += drops.gold;
    await deps.persistence.saveCharacter(character);
    await liberateTown(townTile, pillarSlotTile, deps);
    return 'victory';
  }
  return resolveCombatOutcome(state, character, townTile, deps, drops);
}
